/* eslint-disable no-nested-ternary */
import {
   Box,
   Card,
   Checkbox,
   CircularProgress,
   FormControl,
   IconButton,
   InputLabel,
   MenuItem,
   Select,
   TextField,
   Typography,
   useMediaQuery,
   useTheme,
} from '@mui/material'
import { Add, Link as LinkIcon, Print, RowingTwoTone } from '@mui/icons-material'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Form, FormikProvider, useFormik } from 'formik'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { CSVLink } from 'react-csv'

import { useLogisticMinimumsQuery } from 'shared/store/useLogisticMinimums'
import HiddenWholesalerLogo from 'shared/components/HiddenWholesalerLogo'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import { useNotificationContext } from 'shared/context/Notifications'
import { MinimumType } from 'shared/store/useLogisticMinimums/types'
import invalidateCarts from 'shared/utils/helpers/invalidateCarts'
import { useClientPrices } from 'shared/hooks/ClientPriceHook'
import { useProfileQuery } from 'shared/store/useProfile'
import CustomTable from 'shared/components/CustomTable'
import InputField from 'shared/components/InputField'
import useLogin from 'shared/store/useLogin'
import { noInformationsWholesales, UserRoles } from 'shared/consts'
import { httpClient } from 'shared/lib'
import {
   FetchCartSummaryResult,
   Product,
   RemoveProductPayload,
   UpdateCartInfoPayload,
   UpdateProductType,
} from 'shared/store/useCartSummary/types'
import {
   useCartSummaryQuery,
   useChangeWholesaleIncludeInOrderMutation,
   useNewCartMutation,
   useRemoveProductMutation,
   useUpdateCartInfoMutation,
   useUpdatePricesMutation,
   useUpdateProductMutation,
} from 'shared/store/useCartSummary'

import useCustomersQuery from 'shared/store/useCustomers'
import SelectField from 'shared/components/SelectField'
import { sortAlphabetical } from 'shared/utils/helpers/sorters'
import returnMinimumDeliveryTableCell from './helpers/returnMinimumDeliveryTableCell'
import { CartDetailsType, ModalInfoType, UpdatePricesDiffType } from './types'
import AdditionalServicesTable from './components/AdditionalServicesTable'
import UnavailableProductTable from './components/UnavailableProductTable'
import generateReflink from './helpers/generateReflink'
import MainTable from './components/MainTable'
import Modal from './components/Modal'
import { pl, en, de } from './locales'
import schema from './schema'

export default function MainCart() {
   const { addNotification } = useNotificationContext()
   const [isOrdering, setIsOrdering] = useState(false)
   const hasUpdated = useRef(false)
   const { data: customers } = useCustomersQuery()
   const { i18n } = useTranslation(['TRANSLATION', 'MAIN_CART'])
   const navigate = useNavigate()
   const queryClient = useQueryClient()
   const [cartLocked, setCartLocked] = useState(false)
   const { t } = useTranslation(['TRANSLATION', 'MAIN_CART'])
   const [modalInfo, setModalInfo] = useState<ModalInfoType>()
   const { clientPricesOn } = useClientPrices()
   const [clientView, setClientView] = useState(!!clientPricesOn)
   const [oldData, setOldData] = useState<FetchCartSummaryResult>()
   const { data: summaryData, isFetched, isFetching, isError, error, isLoading } = useCartSummaryQuery()
   const { data: logisticMinimums, isFetched: isLogisticMinimumsFetched } = useLogisticMinimumsQuery(false)
   const { mutate: createNewCartMutate, isLoading: createCartIsLoading } = useNewCartMutation()
   const { mutate: updatePricesMutate, isLoading: updatePriceLoading } = useUpdatePricesMutation()
   const { mutate: updateCartInfoMutate, isLoading: updateCartIsLoading } = useUpdateCartInfoMutation()
   const { mutate: removeProductFromCartMutate } = useRemoveProductMutation()
   const { mutate: changeWholesaleIncludeInOrderMutate } = useChangeWholesaleIncludeInOrderMutation()
   const { mutate, isLoading: isProductUpdating } = useUpdateProductMutation()
   const { user } = useLogin()
   const location = useLocation()
   const [searchParams, setSearchParams] = useSearchParams()
   const theme = useTheme()
   const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
   const { data: profileData } = useProfileQuery()
   i18n.addResourceBundle('pl-PL', 'MAIN_CART', pl)
   i18n.addResourceBundle('en-EN', 'MAIN_CART', en)
   i18n.addResourceBundle('de-DE', 'MAIN_CART', de)

   async function onOrderSubmit() {
      setIsOrdering(true)
      await httpClient
         .post('/orders')
         .then((res) => {
            if (
               summaryData?.products.find((product) => noInformationsWholesales[product.cartWholesaleDTO.wholesaleUuid])
            ) {
               window.open('https://pl.e-cat.intercars.eu/pl/cart', '_blank')
            }
            if (res.status === 200) {
               navigate('/dashboard/order-summary', { state: res.data })
            } else if (res.status === 204) {
               addNotification('processingOrder', 'success')
            }
         })
         .catch(() => {
            addNotification('failedOrder', 'error')
         })
         .finally(() => {
            invalidateCarts(queryClient)
            setIsOrdering(false)
         })
   }

   function generateCSV() {
      if (summaryData) {
         const CSVData: (string | number)[][] = []
         const titleRow = [
            t('TRANSLATION:columnNames.partIndex'),
            t('TRANSLATION:columnNames.productName'),
            t('TRANSLATION:columnNames.producer'),
            t('TRANSLATION:columnNames.wholesaler'),
            t('TRANSLATION:columnNames.quantity'),
            'VAT',
            t('TRANSLATION:columnNames.clientNetPrice'),
            t('TRANSLATION:columnNames.clientGrossPrice'),
            t('TRANSLATION:columnNames.clientNetSum'),
            t('TRANSLATION:columnNames.clientGrossSum'),
            t('TRANSLATION:columnNames.netPrice'),
            t('TRANSLATION:columnNames.grossPrice'),
            t('TRANSLATION:columnNames.netSum'),
            t('TRANSLATION:columnNames.grossSum'),
         ]

         const extractNumber = (text: string) => {
            if (!text) {
               return '-'
            }
            if (text.toLowerCase().includes('pln') || text.toLowerCase().includes('zł')) {
               const cleaned = text.replace(/[^\d.,]/g, '')
               const match = cleaned.match(/\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{2})?/)
               return match ? match[0] : null
            }
            return text
         }

         CSVData.push(titleRow)
         const services = [t('TRANSLATION:columnNames.services')]
         const unavailable = [t('MAIN_CART:unavailableProducts')]
         if (summaryData.products.filter((product) => product.status !== 'NOT_AVAILABLE').length > 0) {
            summaryData.products
               .filter((product) => product.status !== 'NOT_AVAILABLE')
               .forEach((product) => {
                  CSVData.push([
                     product.index,
                     product.cartProductName ? product.cartProductName : product.index,
                     product.producerName,
                     product.cartWholesaleName,
                     product.cartWholesaleDTO.quantity,
                     product.cartWholesaleDTO.taxRate,
                     extractNumber(product.cartWholesaleDTO.formattedClientNetPrice)!,
                     extractNumber(product.cartWholesaleDTO.formattedClientGrossPrice)!,
                     extractNumber(product.cartWholesaleDTO.formattedClientNetValue)!,
                     extractNumber(product.cartWholesaleDTO.formattedClientGrossValue)!,
                     extractNumber(product.cartWholesaleDTO.formattedNetPrice)!,
                     extractNumber(product.cartWholesaleDTO.formattedGrossPrice)!,
                     extractNumber(product.cartWholesaleDTO.formattedNetValue)!,
                     extractNumber(product.cartWholesaleDTO.formattedGrossValue)!,
                  ])
               })
         }
         if (summaryData.products.filter((item) => item.status === 'NOT_AVAILABLE').length > 0) {
            CSVData.push([])
            CSVData.push(unavailable)
            summaryData.products
               .filter((item) => item.status === 'NOT_AVAILABLE')
               .forEach((product) => {
                  CSVData.push([
                     product.index,
                     product.cartProductName ? product.cartProductName : product.index,
                     product.producerName,
                     product.cartWholesaleName,
                     product.cartWholesaleDTO.quantity,
                     product.cartWholesaleDTO.taxRate,
                     extractNumber(product.cartWholesaleDTO.formattedClientNetPrice)!,
                     extractNumber(product.cartWholesaleDTO.formattedClientGrossPrice)!,
                     extractNumber(product.cartWholesaleDTO.formattedClientNetValue)!,
                     extractNumber(product.cartWholesaleDTO.formattedClientGrossValue)!,
                     extractNumber(product.cartWholesaleDTO.formattedNetPrice)!,
                     extractNumber(product.cartWholesaleDTO.formattedGrossPrice)!,
                     extractNumber(product.cartWholesaleDTO.formattedNetValue)!,
                     extractNumber(product.cartWholesaleDTO.formattedGrossValue)!,
                  ])
               })
         }
         if (summaryData.additionalServices.length > 0) {
            CSVData.push([])
            CSVData.push(services)
            summaryData.additionalServices.forEach((service) => {
               CSVData.push([
                  '',
                  service.name,
                  '',
                  '',
                  service.quantity,
                  service.taxRate,
                  '',
                  '',
                  '',
                  '',
                  extractNumber(service.formattedNetPrice)!,
                  extractNumber(service.formattedGrossPrice)!,
                  extractNumber(service.formattedNetValue)!,
                  extractNumber(service.formattedGrossValue)!,
               ])
            })
         }

         return !user.roles.includes(UserRoles.PRO) &&
            !user.roles.includes(UserRoles.ULTRA) &&
            !user.roles.includes(UserRoles.TURBO) &&
            !user.roles.includes(UserRoles.NITRO) ? (
            <OutlinedButton disabled variant="outlined">
               {t('MAIN_CART:generateCsv')}
            </OutlinedButton>
         ) : (
            <CSVLink style={{ textAlign: 'right' }} filename={summaryData.cartDetailsDTO.cartName} data={CSVData}>
               <OutlinedButton variant="outlined">{t('MAIN_CART:generateCsv')}</OutlinedButton>
            </CSVLink>
         )
      }
      return null
   }

   function updateProduct(uuid: string, quantity?: number, includeProductInOrder?: boolean, netClientPrice?: number) {
      if (summaryData) {
         const mutatePayload: UpdateProductType = {
            payload: {
               quantity,
               netClientPrice,
               includeProductInOrder,
            },
            cartUuid: summaryData.cartDetailsDTO.cartUuid,
            cartProductUuid: uuid,
         }
         mutate(mutatePayload, {
            onSuccess: () => {
               invalidateCarts(queryClient)
               setModalInfo(undefined)
            },
         })
      }
   }

   const formik = useFormik<CartDetailsType>({
      initialValues: {
         cartDescription: '',
         extraComments: '',
         cartName: '',
         cartUuid: '',
      },
      validationSchema: schema,
      onSubmit: () => {},
   })

   const { setValues, values, setFieldValue } = formik

   function changeCartName() {
      if (values.cartName === summaryData?.cartDetailsDTO.cartName) {
         addNotification('TRANSLATION:apiErrors.nothingChanged', 'error')
      } else if (values.cartUuid) {
         const payload: UpdateCartInfoPayload = {
            payload: {
               cartName: values.cartName,
            },
            cartUuid: values.cartUuid,
         }
         updateCartInfoMutate(payload)
      }
   }

   function changeCartDescription() {
      if (values.cartDescription === summaryData?.cartDetailsDTO.cartDescription) {
         addNotification('TRANSLATION:apiErrors.nothingChanged', 'error')
      } else if (values.cartUuid) {
         const payload: UpdateCartInfoPayload = {
            payload: {
               cartDescription: values.cartDescription,
            },
            cartUuid: values.cartUuid,
         }
         updateCartInfoMutate(payload)
      }
   }

   function changeExtraComment() {
      if (values.extraComments === summaryData?.cartDetailsDTO.extraComments) {
         addNotification('TRANSLATION:apiErrors.nothingChanged', 'error')
      } else if (values.cartUuid) {
         const payload: UpdateCartInfoPayload = {
            payload: {
               extraComments: values.extraComments,
            },
            cartUuid: values.cartUuid,
         }
         updateCartInfoMutate(payload)
      }
   }

   function updateCustomer(clientUuid: string) {
      if (values.cartUuid) {
         const payload: UpdateCartInfoPayload = {
            payload: {
               clientUuid,
            },
            cartUuid: values.cartUuid,
         }
         updateCartInfoMutate(payload, {
            onSuccess: () => {
               setFieldValue('clientUuid', clientUuid)
               if (searchParams.get('customer')) {
                  searchParams.delete('customer')
                  setSearchParams(searchParams)
               }
            },
         })
      }
   }
   function updateCustomerContact(clientContactUuid: string) {
      if (values.cartUuid) {
         const payload: UpdateCartInfoPayload = {
            payload: {
               clientContactUuid,
            },
            cartUuid: values.cartUuid,
         }
         updateCartInfoMutate(payload, {
            onSuccess: () => {
               setFieldValue('clientContactUuid', clientContactUuid)
            },
         })
      }
   }

   function handleRemoveProduct(uuid: string, removeFromUnavailable?: boolean) {
      if (values.cartUuid) {
         const payload: RemoveProductPayload = {
            cartUuid: values.cartUuid,
            uuid,
            removeFromUnavailable,
         }
         removeProductFromCartMutate(payload, {
            onSettled: () => {
               invalidateCarts(queryClient)
               setModalInfo(undefined)
            },
         })
      }
   }

   function handleUpdateCart(cartUuid: string) {
      setOldData(summaryData)
      updatePricesMutate(cartUuid, {
         onSuccess: (data) => {
            searchParams.delete('orderAgain')
            setSearchParams(searchParams)
            checkDiffs(data.products)
         },
      })
   }

   function checkDiffs(data: Product[]) {
      const updatePriceDiffs: UpdatePricesDiffType = []
      if (oldData && data) {
         oldData.products
            .filter((item) => item.status === 'AVAILABLE')
            .forEach((item) => {
               const newProduct: Product | undefined = data
                  .filter((newItem) => newItem.status === 'AVAILABLE' || newItem.status === 'WHOLESALE_NO_COOPERATION')
                  .find((newItem) => item.uuid === newItem.uuid)

               if (newProduct) {
                  if (newProduct.status === 'WHOLESALE_NO_COOPERATION') {
                     updatePriceDiffs.push({
                        wholesaleName: newProduct.cartWholesaleName,
                        productIndex: newProduct.index,
                        reason: 'NO_COOPERATION',
                     })
                  } else if (newProduct.cartWholesaleDTO.netPrice !== item.cartWholesaleDTO.netPrice) {
                     updatePriceDiffs.push({
                        wholesaleName: newProduct.cartWholesaleName,
                        productIndex: newProduct.index,
                        reason: 'PRICE',
                        priceBefore:
                           profileData?.priceType === 'NET'
                              ? item.cartWholesaleDTO.formattedNetPrice
                              : item.cartWholesaleDTO.formattedGrossPrice,
                        priceAfter:
                           profileData?.priceType === 'NET'
                              ? newProduct.cartWholesaleDTO.formattedNetPrice
                              : newProduct.cartWholesaleDTO.formattedGrossPrice,
                     })
                  } else if (newProduct.cartWholesaleDTO.quantity !== item.cartWholesaleDTO.quantity) {
                     updatePriceDiffs.push({
                        wholesaleName: newProduct.cartWholesaleName,
                        productIndex: newProduct.index,
                        reason: 'AVAILABILITY',
                     })
                  }
               }
            })
      }
      if (updatePriceDiffs.length > 0) {
         setModalInfo({
            modalType: 'AFTER_UPDATE',
            updatePricesDiffs: updatePriceDiffs,
         })
      }
   }

   function returnWholesaleMinimumSum(wholesalerUuid: string) {
      return `${summaryData?.products
         .filter(
            (product) =>
               product.cartWholesaleDTO.wholesaleUuid === wholesalerUuid &&
               product.status === 'AVAILABLE' &&
               product.includedProductInOrder
         )
         .reduce((acc, current) => acc + current.cartWholesaleDTO.netPrice * current.cartWholesaleDTO.quantity, 0)
         .toFixed(2)} zł`
   }

   useEffect(() => {
      if (summaryData) {
         setOldData(summaryData)
         setValues({
            ...summaryData.cartDetailsDTO,
            clientUuid: summaryData.cartDetailsDTO.clientUuid,
            clientContactUuid: summaryData.cartDetailsDTO.clientContactUuid,
            cartDescription: summaryData.cartDetailsDTO.cartDescription ?? '',
            extraComments: summaryData.cartDetailsDTO.extraComments ?? '',
            cartName: summaryData.cartDetailsDTO.cartName ?? '',
         })
      }
   }, [summaryData, isFetching])

   useEffect(() => {
      if (summaryData && customers) {
         setValues((prevState) => ({
            ...prevState,
            extraComments: summaryData.cartDetailsDTO.extraComments ?? '',
            clientUuid: summaryData.cartDetailsDTO.clientUuid,
            clientContactUuid: summaryData.cartDetailsDTO.clientContactUuid,
            cartDescription: summaryData.cartDetailsDTO.cartDescription ?? '',
            cartName: summaryData.cartDetailsDTO.cartName ?? '',
         }))
      }
   }, [summaryData, customers])

   useEffect(() => {
      if (localStorage.getItem('searchBlocked') === 'SUBSCRIPTION_DOWNGRADE') {
         if (user.roles.includes(UserRoles.MAIN_LOCALIZATION_ADMIN)) {
            navigate('/dashboard/wholesales-keeping')
         } else {
            navigate('/dashboard')
         }
      }

      if (localStorage.getItem('searchBlocked') === 'MISSING_PAYMENT') {
         navigate('/dashboard')
      }
   }, [localStorage.getItem('searchBlocked')])

   useEffect(() => {
      setModalInfo(undefined)
   }, [profileData?.locationUuid])

   useEffect(() => {
      if (isError) {
         if (error.message.includes('errors.cart.locked')) {
            setCartLocked(true)
         } else if (error.message.includes('errors.company.is-blocked-due-payment')) {
            window.localStorage.setItem('searchBlocked', 'MISSING_PAYMENT')
            navigate('/dashboard')
         } else if (error.message.includes('errors.company.is-blocked-due-downgrade')) {
            window.localStorage.setItem('searchBlocked', 'SUBSCRIPTION_DOWNGRADE')
            navigate('/dashboard')
         } else {
            navigate('/dashboard/error-page', {
               state: {
                  variant: 'backend',
                  path: location.pathname,
                  error: {
                     message: error,
                     stack: 'unknown',
                  },
               },
            })
         }
      } else {
         window.localStorage.removeItem('searchBlocked')
      }
   }, [isError, isLoading])

   useEffect(() => {
      queryClient.invalidateQueries('userProfile')
   }, [])

   useEffect(() => {
      if (!hasUpdated.current && searchParams.get('orderAgain') && summaryData) {
         setTimeout(() => {
            if (!updatePriceLoading) {
               hasUpdated.current = true
               handleUpdateCart(summaryData.cartDetailsDTO.cartUuid!)
            }
         }, 2000)
      }
   }, [searchParams, summaryData])

   useEffect(() => {
      if (clientPricesOn) {
         setClientView(true)
      }
   }, [clientPricesOn])

   useEffect(() => {
      setTimeout(() => {
         queryClient.invalidateQueries('mainCart')
      }, 500)
   }, [i18n.language])

   useEffect(() => {
      if (searchParams.get('customer')) {
         const uuid = searchParams.get('customer')!
         updateCustomer(uuid)
      }
   }, [values.cartUuid])

   return (
      <Box>
         <FormikProvider value={formik}>
            <Form>
               {isFetched && !isError && summaryData ? (
                  <>
                     <Typography variant="body1" color="primary" sx={{ mb: '8px' }}>
                        {t('MAIN_CART:availableProducts')}
                     </Typography>
                     <Card sx={{ backgroundColor: 'rgba(33, 147, 224, 0.1)' }}>
                        <Box
                           sx={{
                              display: 'flex',
                              m: '16px 16px 0px 16px',
                              flexDirection: { lg: 'row', md: 'row', xs: 'column' },
                              gap: '12px',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                           }}
                        >
                           <Box
                              sx={{
                                 display: 'flex',
                                 flexDirection: { lg: 'row', md: 'row', xs: 'column' },
                                 gap: '8px',
                              }}
                           >
                              <InputField<CartDetailsType>
                                 name="cartName"
                                 onBlur={changeCartName}
                                 onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                       e.preventDefault()
                                       e.stopPropagation()
                                       changeCartName()
                                    }
                                 }}
                                 disabled={updateCartIsLoading}
                                 fullWidth={false}
                                 slotProps={{
                                    htmlInput: {
                                       maxLength: 50,
                                    },
                                 }}
                                 variant="standard"
                                 onFocus={(e) => e.target.select()}
                                 label={t('MAIN_CART:cartName')}
                              />
                              {customers && customers.length > 0 && (
                                 <SelectField
                                    customFields={[
                                       <MenuItem
                                          sx={{
                                             display: 'flex',
                                             alignItems: 'center',
                                             gap: '16px',
                                             justifyContent: 'center',
                                          }}
                                          onClick={(e) => {
                                             e.preventDefault()
                                             e.stopPropagation()
                                             navigate('/dashboard/customers?new=true')
                                          }}
                                       >
                                          <Typography>{t('MAIN_CART:addNewCustomer')}</Typography>
                                          <Add />
                                       </MenuItem>,
                                    ]}
                                    onBlur={(e) => updateCustomer(e.target.value)}
                                    searcher
                                    options={[
                                       ...customers
                                          .sort((a, b) => sortAlphabetical(a.displayName, b.displayName))
                                          .map((customer) => ({
                                             name: customer.displayName,
                                             value: customer.uuid,
                                          })),
                                    ]}
                                    name="clientUuid"
                                    label={t('TRANSLATION:columnNames.client')}
                                 />
                              )}
                              {customers && values.clientUuid && (
                                 <SelectField
                                    searcher
                                    onBlur={(e) => updateCustomerContact(e.target.value)}
                                    options={
                                       customers?.find((customer) => customer.uuid === values.clientUuid)
                                          ? [
                                               ...customers
                                                  .find((customer) => customer.uuid === values.clientUuid)!
                                                  .contacts.sort((a, b) => sortAlphabetical(a.name, b.name))
                                                  .map((contact) => ({
                                                     name: `${contact.name} ${contact.surname}`,
                                                     value: contact.uuid,
                                                  })),
                                            ]
                                          : []
                                    }
                                    name="clientContactUuid"
                                    label={t('TRANSLATION:columnNames.contactPerson')}
                                 />
                              )}
                           </Box>
                           <Box sx={{ display: 'flex', gap: 1 }}>
                              {!clientPricesOn && (
                                 <Box sx={{ display: 'flex', gap: 1 }}>
                                    {clientView ? (
                                       <ContainedButton
                                          type="button"
                                          variant="contained"
                                          onClick={() => setClientView((prevState) => !prevState)}
                                       >
                                          {t('MAIN_CART:clientView')}
                                       </ContainedButton>
                                    ) : (
                                       <OutlinedButton
                                          type="button"
                                          variant="outlined"
                                          onClick={() => setClientView((prevState) => !prevState)}
                                       >
                                          {t('MAIN_CART:clientView')}
                                       </OutlinedButton>
                                    )}
                                 </Box>
                              )}

                              <ContainedButton
                                 type="button"
                                 disabled={createCartIsLoading}
                                 onClick={createNewCartMutate}
                                 variant="contained"
                                 color="primary"
                              >
                                 {t('MAIN_CART:createCart')}
                              </ContainedButton>
                           </Box>
                        </Box>
                        <MainTable
                           formattedClientGrossValue={summaryData.formattedClientGrossValue}
                           formattedClientNetValue={summaryData.formattedClientNetValue}
                           formattedGrossValue={summaryData.formattedGrossValue}
                           formattedNetValue={summaryData.formattedNetValue}
                           clientView={clientView}
                           setModalInfo={setModalInfo}
                           products={summaryData.products}
                           handleProductUpdate={updateProduct}
                           isProductUpdating={isProductUpdating}
                        />
                     </Card>
                     {summaryData.additionalServices.length > 0 && (
                        <AdditionalServicesTable
                           formattedAdditionalServicesGrossValue={summaryData.formattedAdditionalServicesGrossValue}
                           formattedAdditionalServicesNetValue={summaryData.formattedAdditionalServicesNetValue}
                           additionalServices={summaryData.additionalServices}
                           cartUuid={summaryData.cartDetailsDTO.cartUuid}
                           setModalInfo={setModalInfo}
                           clientView={clientView}
                        />
                     )}
                     {!clientView &&
                        summaryData.products.filter((item) => item.status === 'NOT_AVAILABLE').length > 0 && (
                           <UnavailableProductTable
                              products={summaryData.products.filter((item) => item.status === 'NOT_AVAILABLE')}
                              setModalInfo={setModalInfo}
                           />
                        )}
                     <Box
                        sx={{
                           display: 'flex',
                           justifyContent: 'flex-end',
                           gap: 5,
                           margin: '0 auto',
                           paddingTop: 2,
                        }}
                     >
                        <Box sx={{ display: 'flex', gap: 1 }}>
                           <Typography color="grayText" variant="h6">
                              {summaryData.additionalServices.length > 0
                                 ? `${t('TRANSLATION:columnNames.clientNetSumWithServices')}:`
                                 : `${t('TRANSLATION:columnNames.clientNetSum')}:`}
                           </Typography>
                           <Typography variant="h6">
                              {summaryData.formattedClientNetValueWithAdditionalServices}
                           </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                           <Typography variant="h6" color="grayText">
                              {summaryData.additionalServices.length > 0
                                 ? `${t('TRANSLATION:columnNames.clientGrossSumWithServices')}:`
                                 : `${t('TRANSLATION:columnNames.clientGrossSum')}:`}
                           </Typography>
                           <Typography variant="h6">
                              {summaryData.formattedClientGrossValueWithAdditionalServices}
                           </Typography>
                        </Box>
                     </Box>
                     <Box
                        sx={{
                           display: 'flex',
                           justifyContent: 'flex-end',
                           flexWrap: 'wrap',
                           gap: 2,
                           margin: '0 auto',
                           paddingTop: 2,
                        }}
                     >
                        <OutlinedButton
                           sx={{ alignSelf: 'flex-start' }}
                           onClick={() =>
                              setModalInfo({
                                 modalType: 'SERVICES',
                                 uuid: summaryData.cartDetailsDTO.cartUuid,
                              })
                           }
                           variant="outlined"
                        >
                           {t('MAIN_CART:services.addService')}
                        </OutlinedButton>

                        {generateCSV()}

                        <OutlinedButton
                           disabled={
                              summaryData?.products.length === 0 ||
                              (!user.roles.includes(UserRoles.NITRO) &&
                                 !user.roles.includes(UserRoles.TURBO) &&
                                 !user.roles.includes(UserRoles.SPORT) &&
                                 !user.roles.includes(UserRoles.TEST_DRIVE) &&
                                 !user.roles.includes(UserRoles.ULTRA) &&
                                 !user.roles.includes(UserRoles.PRO) &&
                                 !user.roles.includes(UserRoles.BASIC))
                           }
                           onClick={() =>
                              generateReflink({
                                 summaryData,
                                 setModalInfo,
                                 onFailed: () => addNotification(t('MAIN_CART:generateFailed'), 'error'),
                              })
                           }
                           variant="outlined"
                        >
                           <LinkIcon />
                        </OutlinedButton>
                        {!isMobile && (
                           <OutlinedButton
                              disabled={summaryData?.products.length === 0}
                              onClick={() =>
                                 setModalInfo({
                                    modalType: 'PRINT',
                                    pricingVariant: clientView ? 'CLIENT' : 'FULL',
                                 })
                              }
                              variant="outlined"
                           >
                              <Print color={summaryData?.products.length === 0 ? 'disabled' : 'primary'} />
                           </OutlinedButton>
                        )}
                        {!user.roles.includes(UserRoles.DEMO) && !user.roles.includes(UserRoles.DEMO_SUBSCRIPTION) ? (
                           <OutlinedButton
                              disabled={updatePriceLoading || summaryData?.products.length === 0}
                              onClick={() => {
                                 if (summaryData) {
                                    handleUpdateCart(summaryData.cartDetailsDTO.cartUuid)
                                 }
                              }}
                              variant="outlined"
                           >
                              {t('MAIN_CART:updatePrices')}
                           </OutlinedButton>
                        ) : (
                           <OutlinedButton variant="outlined">{t('MAIN_CART:updatePrices')}</OutlinedButton>
                        )}
                        <ContainedButton
                           onClick={onOrderSubmit}
                           disabled={
                              isProductUpdating ||
                              summaryData?.products.length === 0 ||
                              !summaryData?.products.find((item) => item.includedProductInOrder) ||
                              isOrdering ||
                              user?.roles.includes(UserRoles.SALES_REP)
                           }
                           variant="contained"
                        >
                           {t('MAIN_CART:order')}
                        </ContainedButton>
                     </Box>
                     {!clientView && (
                        <Box
                           sx={{
                              display: 'flex',
                              flexDirection: { lg: 'row', md: 'row', xs: 'column' },
                              gap: 3,
                              margin: '0 auto',
                              marginTop: 6,
                              minHeight: 200,
                           }}
                        >
                           {logisticMinimums?.logisticMinimums && logisticMinimums.logisticMinimums.length > 0 && (
                              <Box
                                 sx={{
                                    flex: { lg: 0.5, md: 0.5, xs: 1 },
                                 }}
                              >
                                 {isLogisticMinimumsFetched && logisticMinimums ? (
                                    <CustomTable
                                       dependencies={[]}
                                       rows={logisticMinimums.logisticMinimums}
                                       disablePagination
                                       dataSize={logisticMinimums.logisticMinimums.length}
                                       columns={[
                                          {
                                             name: 'wholesaler',
                                             renderCell: (row: MinimumType) => (
                                                <HiddenWholesalerLogo
                                                   wholesalerUuid={row.wholesaleUuid}
                                                   wholesalerName={row.wholesaleName}
                                                   imgHeight={isMobile ? '15px' : '20px'}
                                                   imgWidth="auto"
                                                />
                                             ),
                                             mainOnMobile: true,
                                          },
                                          {
                                             name: 'includeInOrder',
                                             renderCell: (row: MinimumType) => (
                                                <Checkbox
                                                   onChange={(e) => {
                                                      changeWholesaleIncludeInOrderMutate({
                                                         payload: {
                                                            wholesaleUuid: row.wholesaleUuid,
                                                            includeProductInOrder: e.target.checked,
                                                         },
                                                         cartUuid: summaryData!.cartDetailsDTO.cartUuid!,
                                                      })
                                                   }}
                                                   checked={summaryData.products
                                                      .filter(
                                                         (product) =>
                                                            product.cartWholesaleDTO.wholesaleUuid === row.wholesaleUuid
                                                      )
                                                      .some((prod) => prod.includedProductInOrder)}
                                                />
                                             ),
                                          },
                                          {
                                             name: 'wholesaleProductsSumNet',
                                             renderCell: (row: MinimumType) =>
                                                returnWholesaleMinimumSum(row.wholesaleUuid),
                                          },
                                          {
                                             name: 'remainLogisticMinimumsNet',
                                             renderCell: (row: MinimumType) => row.formattedRemainingAmount,
                                             mainOnMobile: true,
                                          },
                                          {
                                             name: 'timeUntilDeliveryStart',
                                             renderCell: (row: MinimumType) =>
                                                returnMinimumDeliveryTableCell(row.minutesToDeliveryStart),
                                             mainOnMobile: true,
                                          },
                                       ]}
                                    />
                                 ) : (
                                    <CircularProgress sx={{ m: '0 auto' }} color="primary" />
                                 )}
                              </Box>
                           )}
                           <Box
                              sx={{
                                 display: 'flex',
                                 flexDirection: 'column',
                                 ml: { lg: 'auto', md: 'auto', xs: 0 },
                                 flex: { lg: 0.5, md: 0.5, xs: 1 },
                                 gap: 4,
                              }}
                           >
                              <InputField<CartDetailsType>
                                 name="extraComments"
                                 onBlur={changeExtraComment}
                                 disabled={updateCartIsLoading}
                                 multiline
                                 rows={3}
                                 label={t('MAIN_CART:extraComments')}
                              />
                              <InputField<CartDetailsType>
                                 name="cartDescription"
                                 onBlur={changeCartDescription}
                                 disabled={updateCartIsLoading}
                                 multiline
                                 rows={3}
                                 label={t('MAIN_CART:cartDescription')}
                              />
                           </Box>
                        </Box>
                     )}
                     <Modal
                        modalInfo={modalInfo}
                        data={summaryData}
                        handleClose={() => setModalInfo(undefined)}
                        handleProductUpdate={updateProduct}
                        handleRemoveProductFromCart={handleRemoveProduct}
                     />
                  </>
               ) : cartLocked ? (
                  <Card
                     sx={{
                        p: {
                           lg: '24px 16px',
                           md: '16px 8px',
                           xs: '12px 6px',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           flexDirection: 'column',
                           gap: '8px',
                        },
                     }}
                  >
                     <Typography color="error" variant={isMobile ? 'h6' : 'h4'}>
                        {t('MAIN_CART:cartLockedError')}
                     </Typography>
                     <ContainedButton
                        type="button"
                        disabled={createCartIsLoading}
                        onClick={createNewCartMutate}
                        variant="contained"
                        color="primary"
                     >
                        {t('MAIN_CART:createCart')}
                     </ContainedButton>
                  </Card>
               ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                     <CircularProgress />
                  </Box>
               )}
            </Form>
         </FormikProvider>
      </Box>
   )
}
